<template>
    <div class="sign-in-screen">
        <div class="sign-in-tile">
            <Dropdown
                v-model="myLocale"
                :options="languages"
                optionLabel="name"
                optionValue="value"
                data-id="language-selector"
                :placeholder="locale | upper"
                @change="setLanguage"
            />
            <Sidebar :version="getVersion" />
            <div class="sign-in">
                <h2 data-id="login-heading">{{ header }}</h2>
                <p v-if="text">{{ text }}</p>
                <p v-show="hasErrors" class="error-login">
                    {{ $t(errorMessage) }}
                </p>
                <div v-show="waiting" class="waiting">
                    <i class="pi pi-spin pi-spinner" style="font-size: 3em"></i
                    ><br />
                    {{ $t('wait') }}
                </div>
                <div v-show="!waiting">
                    <LogIn @submit="doSubmit" v-if="key === 'login'" />
                    <Tfa @submit="doSubmit" v-if="key === 'tfa'" />
                    <TfaActivation @submit="doSubmit" v-if="key === 'tfaActivation'" />
                    <ForgotPassword
                        @submit="doSubmit"
                        v-if="key === 'forgotPassword'"
                        :sent.sync="sent"
                    />
                    <TfaAnon
                        @submit="doSubmit"
                        v-if="newPasswordReady && tfaRequired"
                    />
                    <NewPassword
                        @submit="doSubmit"
                        v-if="newPasswordReady && !tfaRequired"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import Sidebar from 'myra-ui-common/components/sidebar/Sidebar';
    import LogIn from '../../components/authentication/login/Login';
    import Tfa from '../../components/authentication/tfa/Tfa';
    import TfaActivation from '../../components/authentication/tfa/TfaActivation';
    import TfaAnon from '../../components/authentication/tfa/TfaAnon';
    import upper from 'myra-ui-common/utils/upper';
    import ForgotPassword from '../../components/authentication/forgotPassword/ForgotPassword';
    import NewPassword from '../../components/authentication/newPassword/NewPassword';
    import { mapGetters } from 'vuex'

    export default {
        name       : 'Authentication',
        components : {
            LogIn,
            NewPassword,
            ForgotPassword,
            Tfa,
            TfaAnon,
            TfaActivation,
            Dropdown,
            Sidebar,
        },
        data() {
            return {
                errorMessage : '',
                locale       : this.$localStorage.get('locale') || 'en',
                waiting      : false,
                sent         : false,
                myLocale     : this.$localStorage.get('locale'),
                tfaConfirmed : false,
                isTfa        : false,
                tfaSent      : false,
            };
        },
        watch : {
            key() {
                if (this.key === 'forgotPassword') {
                    this.sent = false;
                }
            },
        },
        created() {
            if (this.key === 'newPassword') {
                this.clearSession();
                this.hasTfa();
            }
        },
        computed : {
          ...mapGetters(['getVersion']),
          languages() {
                return this.$store.getters.getLanguages;
            },
            hasErrors() {
                return this.errorMessage.length > 0;
            },
            key() {
                return this.$route.name;
            },
            header() {
                return this.$t(this.key + '.header');
            },
            text() {
                const key = this.key + '.text';
                const tr = this.$t(key);

                if (this.sent && this.key === 'forgotPassword') {
                    return null
                }

                return tr === key ? null : tr;
            },
            newPasswordReady() {
                return this.key === 'newPassword' && this.tfaSent;
            },
            tfaRequired() {
                return this.isTfa === true && this.tfaConfirmed === false;
            }
        },
        filters : {
            upper,
        },
        methods : {
            setLanguage() {
                this.$root.$i18n.locale = this.myLocale;
                this.$localStorage.set('locale', this.myLocale);
            },
            clearSession() {
                this.$store.commit('resetLogin');
            },
            hasTfa() {
                this.$apiClient
                    .get('isTfa', { token : this.$route.params.token })
                    .then((response) => {
                        this.tfaSent = true;
                        let errorMsg = response.data.msg;
                        let view = response.data.view;
                        if (errorMsg !== undefined) {
                            this.$notifier.notifyError(errorMsg, '', 5000);
                        }

                        if (view === 'reset') {
                            this.isTfa = false;
                            this.tfaConfirmed = false;
                        }

                        if (view === 'tfa') {
                            this.isTfa = true;
                            this.tfaConfirmed = false;
                        }
                    })
                    .catch((error) => {
                        this.tfaSent = true;
                        this.isTfa = false;
                        this.tfaConfirmed = false;
                        const errorMsg = typeof error === 'string' ? error : this.$i18n.t('errors.default');
                        this.$notifier.notifyError(errorMsg, '', 5000);
                    });
            },
            doSubmit(route, data, redirect = null) {
                this.errorMessage = '';
                this.waiting = true;
                this.sent = false;

                this.$store
                    .dispatch(route, data)
                    .then(() => {
                        this.sent = true;
                        if (route === 'updatePassword') {
                            this.$notifier.notifySuccess(this.$i18n.t('toasts.passwordUpdated'));
                        }
                        if (!redirect) {
                            return;
                        }

                        this.$router.push({ name : redirect, query : {redirect : this.$route.query.redirect} });
                    })
                    .catch((error) => {
                        switch (error.status) {
                            case 400:
                            case 403:
                            case 500:
                                this.errorMessage = 'errors.' + error.status;
                                break;
                            case 401:
                                if (error.data && error.data.error === 'password_change_required') {
                                    this.errorMessage = 'errors.401_password_expired';
                                } else if (route === 'sendTfa') {
                                    this.errorMessage = 'errors.401_tfa';
                                } else {
                                    this.errorMessage = 'errors.401';
                                }
                                break;
                            default:
                                this.errorMessage = 'errors.default';
                        }
                    })
                    .finally(() => (this.waiting = false));
            },
        },
    };
</script>

<i18n src="myra-ui-common/i18n/common.json" />
<i18n src="./i18n.json" />
